package globals

import globals.ClientDebug
import org.ttt.autogenesis.config.ConfigSource
import org.ttt.autogenesis.network.GrpcRpcBridgeConfig
import org.ttt.autogenesis.network.ServerExtendTransport
import org.ttt.autogenesis.fleetcontrol.protocol.ServerEndpoint

/**
 * Holds the endpoints and transport mode that the client should use when talking
 * to the server-extend service.
 */
object ServerExtendConfig
{
    private const val LOCAL_GRPC_PORT = 9092
    private const val LIVE_GRPC_PORT = 9091

    /**
     * URL that points at the local server-extend instance used during development.
     */
    var localServerUrl: String = "http://127.0.0.1:7070"

    /**
     * URL that targets the live server-extend deployment inside AccelByte.
     * The configured public service URL is preferred because AccelByte Extend
     * exposes each application below a tenant-specific path. The hostname is
     * retained as a fallback for older deployments that do not provide the
     * optional URL override.
     */
    var liveServerUrl: String = ""
        get() = resolveLiveServerUrl(
            configuredBaseUrl = configuredLiveServerBaseUrl(),
            hostname = ConfigSource.propertyOrEmpty("kvision-iam.local.properties", "kvision.liveHostname")
        )
        set(value) { field = value }

    /**
     * gRPC endpoint that points at the local server-extend bridge.
     */
    var localGrpcUrl: String = GrpcRpcBridgeConfig.development(LOCAL_GRPC_PORT)

    /**
     * gRPC endpoint that targets the live server-extend bridge.
     */
    var liveGrpcUrl: String = ""
        get() = GrpcRpcBridgeConfig.production(
            ConfigSource.propertyOrEmpty("kvision-iam.local.properties", "kvision.liveHostname"),
            LIVE_GRPC_PORT
        )
        set(value) { field = value }

    /**
     * Browser-safe grpc-web base URL for the local server-extend gRPC listener.
     */
    val localGrpcWebUrl: String
        get() = GrpcRpcBridgeConfig.developmentWeb(LOCAL_GRPC_PORT)

    /**
     * Browser-safe grpc-web base URL for the live server-extend gRPC listener.
     */
    val liveGrpcWebUrl: String
        get() = GrpcRpcBridgeConfig.productionWeb(
            ConfigSource.propertyOrEmpty("kvision-iam.local.properties", "kvision.liveHostname"),
            LIVE_GRPC_PORT
        )
        
    /**
     * Resolves the public REST base URL used by the live server-extend bridge.
     *
     * @param configuredBaseUrl Optional public service URL, including any
     *   AccelByte Extend path prefix.
     * @param hostname Tenant hostname used when [configuredBaseUrl] is absent.
     * @return A normalized URL without a trailing slash.
     */
    internal fun resolveLiveServerUrl(configuredBaseUrl: String, hostname: String): String
    {
        val normalizedConfiguredBaseUrl = configuredBaseUrl.trim().trimEnd('/')
        if(normalizedConfiguredBaseUrl.isNotBlank())
        {
            return normalizedConfiguredBaseUrl
        }

        return "https://${hostname.trim().trimEnd('/')}"
    }

    /**
     * Reads the live service URL from the bundled deployment configuration.
     * The IAM properties file is always shipped with the browser bundle, while
     * the broader local properties file remains an explicit local override.
     *
     * @return The configured server-extend public URL, or an empty string when
     *   neither optional configuration file provides one.
     */
    private fun configuredLiveServerBaseUrl(): String
    {
        val iamConfiguredBaseUrl = ConfigSource.propertyOrEmpty(
            "kvision-iam.local.properties",
            "kvision.serverExtend.liveBaseUrl"
        )
        if(iamConfiguredBaseUrl.isNotBlank())
        {
            return iamConfiguredBaseUrl
        }

        return ConfigSource.propertyOrEmpty(
            "kvision.local.properties",
            "kvision.serverExtend.liveBaseUrl"
        )
    }

    /**
     * Transport mode used by the server-extend facade.
     */
    var transport: ServerExtendTransport = ServerExtendTransport.REST_SSE

    private enum class ServerTarget
    {
        LOCAL,
        LIVE
    }

    private var manualTarget: ServerTarget? = null
    private var resolvedServerExtendEndpoint: ServerEndpoint? = null

    /**
     * Binds the browser REST bridge to the lease returned by Fleet Control.
     *
     * @param endpoint Endpoint returned by the public readiness route.
     */
    fun bindResolvedServerExtendEndpoint(endpoint: ServerEndpoint)
    {
        require(endpoint.url.startsWith("http://") || endpoint.url.startsWith("https://"))
        {
            "server-extend endpoint must use http or https"
        }
        require(endpoint.portRole == org.ttt.autogenesis.fleetcontrol.protocol.EndpointPortRole.REST)
        {
            "browser server-extend endpoint must use the REST named port"
        }
        require(!endpoint.grpcUrl.isNullOrBlank())
        {
            "live server-extend lease must expose the named gRPC port"
        }
        resolvedServerExtendEndpoint = endpoint
        manualTarget = null
        // AMS's allocated REST port is not reachable for this deployment
        // contract because the image receives the literal default-port
        // placeholder. The same lease also exposes the reachable named gRPC
        // port, which is the supported browser transport for live RPCs.
        transport = ServerExtendTransport.GRPC_WEB
    }

    /** Clears the current lease binding after a failed connection or logout. */
    fun clearResolvedServerExtendEndpoint()
    {
        resolvedServerExtendEndpoint = null
    }

    /**
     * Marks the default path as the local server so RPC consumers can switch
     * to the localhost bridge after the probe succeeds.
     */
    fun useLocalServer()
    {
        manualTarget = ServerTarget.LOCAL
    }

    /**
     * Reverts the default path back to the live URL when the probe cannot reach
     * a local server-extend instance.
     */
    fun useLiveServer()
    {
        manualTarget = ServerTarget.LIVE
    }

    /**
     * Marks the server-extend facade to use the REST/SSE transport.
     */
    fun useRestTransport()
    {
        transport = ServerExtendTransport.REST_SSE
    }

    /**
     * Marks the server-extend facade to use the bidirectional gRPC transport.
     */
    fun useGrpcBidiTransport()
    {
        transport = ServerExtendTransport.GRPC_BIDI
    }

    /**
     * Marks the server-extend facade to use the grpc-web transport.
     */
    fun useGrpcWebTransport()
    {
        transport = ServerExtendTransport.GRPC_WEB
    }

    /**
     * Returns the current RPC base URL.
     *
     * Any manual target override (set via [useLocalServer] or
     * [useLiveServer]) takes precedence over the build-time debug default.
     */
    val defaultServerUrl: String
        get() = when
        {
            resolvedServerExtendEndpoint != null -> resolvedServerExtendEndpoint!!.url.trimEnd('/')
            manualTarget == ServerTarget.LOCAL -> localServerUrl
            manualTarget == ServerTarget.LIVE -> liveServerUrl
            ClientDebug.debugMode -> localServerUrl
            else -> liveServerUrl
        }

    /**
     * Returns the current gRPC endpoint URL.
     */
    val defaultGrpcServerUrl: String
        get() = when
        {
            resolvedServerExtendEndpoint?.grpcUrl != null -> resolvedServerExtendEndpoint!!.grpcUrl!!
            manualTarget == ServerTarget.LOCAL -> localGrpcUrl
            manualTarget == ServerTarget.LIVE -> liveGrpcUrl
            ClientDebug.debugMode -> localGrpcUrl
            else -> liveGrpcUrl
        }

    /**
     * Returns the current grpc-web listener URL.
     */
    val defaultGrpcWebServerUrl: String
        get() = when
        {
            resolvedServerExtendEndpoint?.grpcUrl != null -> "http://${resolvedServerExtendEndpoint!!.grpcUrl}"
            manualTarget == ServerTarget.LOCAL -> localGrpcWebUrl
            manualTarget == ServerTarget.LIVE -> liveGrpcWebUrl
            ClientDebug.debugMode -> localGrpcWebUrl
            else -> liveGrpcWebUrl
        }
}
